import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Pagination,
  Badge,
} from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useHighlightedStores } from '../../context/HighlightedStoresContext';
import api from '../../services/api';

const WarehouseReturnsPage = () => {
  const { user } = useAuth();
  const { isWarehouseReturnRowHighlighted } = useHighlightedStores();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [returns, setReturns] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  
  // Filter states
  const [periodType, setPeriodType] = useState('created_at');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Additional filters
  const [clientIds, setClientIds] = useState('');
  const [selectedMarketplaces, setSelectedMarketplaces] = useState([]);
  const [statuses, setStatuses] = useState('');
  const [selectedLogisticServices, setSelectedLogisticServices] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');
  
  // Available options
  const marketplaceOptions = ['wildberries', 'ozon', 'market', 'avito', 'leroy', 'aliexpress', 'sber'];
  const logisticServiceOptions = ['wildberries', 'ozon', 'market', 'avito', 'leroy', 'aliexpress', 'sber', 'russian-post', 'cdek'];
  
  
  // Load returns
  const loadReturns = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: currentPage,
        size: pageSize,
      };
      
      // Add period filter if dates are provided
      if (dateFrom || dateTo) {
        params.period = {};
        if (dateFrom) {
          params.period.from = dateFrom;
        }
        if (dateTo) {
          params.period.to = dateTo;
        }
        params.period_type = periodType;
      }
      
      // Build filter object
      const filter = {};
      
      // Client IDs - parse comma or newline separated values
      if (clientIds.trim()) {
        filter.client_ids = clientIds.split(/[,\n]/).map(id => id.trim()).filter(id => id);
      }
      
      // Marketplaces
      if (selectedMarketplaces.length > 0) {
        filter.marketplaces = selectedMarketplaces;
      }
      
      // Statuses - parse comma or newline separated values
      if (statuses.trim()) {
        filter.statuses = statuses.split(/[,\n]/).map(s => s.trim()).filter(s => s);
      }
      
      // Logistic services
      if (selectedLogisticServices.length > 0) {
        filter.logistic_services = selectedLogisticServices;
      }
      
      // Search
      if (searchFilter.trim()) {
        filter.search = searchFilter.trim();
      }
      
      // Updated at
      if (updatedAt) {
        filter.updated_at = updatedAt;
      }
      
      // Add filter to params if it has any values
      if (Object.keys(filter).length > 0) {
        params.filter = filter;
      }
      
      const response = await api.getWarehouseReturns(params);
      if (response.data) {
        setReturns(response.data || []);
        setTotalItems(response.pagination?.total || 0);
        setTotalPages(response.pagination?.total ? Math.ceil(response.pagination.total / pageSize) : 0);
      }
    } catch (err) {
      setError(err.data?.errorText || err.message || 'Ошибка загрузки возвращенных заказов');
      console.error('Failed to load returns:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, dateFrom, dateTo, periodType, clientIds, selectedMarketplaces, statuses, selectedLogisticServices, searchFilter, updatedAt]);

  useEffect(() => {
    if (user?.is_superuser) {
      loadReturns();
    }
  }, [loadReturns, user?.is_superuser]);

  // Handle search/filter
  const handleApplyFilters = () => {
    setCurrentPage(1);
    loadReturns();
  };

  // Find user by order_id and get/create chat
  const findUserAndChat = async (orderId) => {
    try {
      // Try to find the order in our system by order_id (platform_order_no)
      const orderResponse = await api.getAdminOrders({
        search: orderId,
        page: 1,
        page_size: 1,
      });
      
      if (orderResponse.data && orderResponse.data.length > 0) {
        const order = orderResponse.data[0];
        
        // Find user by seller_id (store owner)
        if (order.seller_id) {
          // Search orders by seller_id to get user_info from response
          const userOrderResponse = await api.getAdminOrders({
            seller_id: order.seller_id,
            page: 1,
            page_size: 1,
          });
          
          if (userOrderResponse.user_info) {
            const userId = userOrderResponse.user_info.id;
            const userEmail = userOrderResponse.user_info.email;
            
            // Find or create chat
            let chatId = null;
            try {
              const chatsResponse = await api.getSupportChats();
              const userChat = chatsResponse.data?.find(chat => chat.user === userId);
              if (userChat) {
                chatId = userChat.id;
              } else {
                // Create new chat
                const newChatResponse = await api.createSupportChat({
                  user_id: userId,
                  subject: `Возврат заказа ${orderId}`,
                });
                if (newChatResponse.data) {
                  chatId = newChatResponse.data.id;
                }
              }
            } catch (chatErr) {
              console.error('Failed to get/create chat:', chatErr);
            }
            
            return { userId, userEmail, chatId, order };
          }
        }
      }
      
      return null;
    } catch (err) {
      console.error('Failed to find user:', err);
      return null;
    }
  };

  // Open support chat for return user
  const openSupportChat = async (returnData) => {
    try {
      setError('');
      
      if (!returnData.order_id) {
        setError('Не указан ID заказа');
        return;
      }
      
      const result = await findUserAndChat(returnData.order_id);
      
      if (result) {
        // Navigate to admin support page with chat
        if (result.chatId) {
          window.location.href = `/?view=adminSupport&chat=${result.chatId}`;
        } else {
          window.location.href = `/?view=adminSupport&user_id=${result.userId}`;
        }
      } else {
        setError('Не удалось найти пользователя по заказу ' + returnData.order_id);
      }
    } catch (err) {
      setError('Не удалось открыть чат техподдержки: ' + (err.data?.errorText || err.message));
    }
  };

  // Request QR code for return
  const requestQRCode = async (returnData) => {
    try {
      setError('');
      
      if (!returnData.order_id) {
        setError('Не указан ID заказа');
        return;
      }
      
      const result = await findUserAndChat(returnData.order_id);
      
      if (!result || !result.chatId) {
        setError('Не удалось найти пользователя или создать чат');
        return;
      }
      
      // Get platform_order_no from order
      const platformOrderNo = result.order?.platform_order_no || returnData.order_id;
      
      // Send message with QR code request
      const message = `Здравствуйте! Товар, проданный с нашего склада: ${platformOrderNo} был возвращен и его необходимо забрать со склада Wildberries. Пожалуйста, предоставьте QR код для получения товара.`;
      
      try {
        await api.sendSupportMessage(result.chatId, message);
        
        // Open chat to show the message
        window.location.href = `/?view=adminSupport&chat=${result.chatId}`;
      } catch (msgErr) {
        setError('Не удалось отправить сообщение: ' + (msgErr.data?.errorText || msgErr.message));
      }
    } catch (err) {
      setError('Не удалось запросить QR код: ' + (err.data?.errorText || err.message));
    }
  };

  // Check if user is superuser
  if (!user?.is_superuser) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          Доступ запрещен. Только администраторы могут просматривать эту страницу.
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4 warehouse-returns-page" fluid>
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-transparent border-bottom">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="mb-0 fw-bold" style={{ color: 'var(--bs-body-color)' }}>
              Возвращенные заказы
            </h4>
          </div>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Filters */}
          <>
              <Row className="mb-3 g-3">
                <Col xs={12}>
                  <h6 className="mb-2" style={{ color: 'var(--bs-body-color)' }}>Период</h6>
                </Col>
                <Col xs={12} sm={6} md={3}>
                  <Form.Label className="small">Тип периода</Form.Label>
                  <Form.Select
                    value={periodType}
                    onChange={(e) => setPeriodType(e.target.value)}
                  >
                    <option value="created_at">По дате создания</option>
                    <option value="processed_at">По дате обработки</option>
                  </Form.Select>
                </Col>
                <Col xs={6} sm={3} md={3}>
                  <Form.Label className="small">От</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </Col>
                <Col xs={6} sm={3} md={3}>
                  <Form.Label className="small">До</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </Col>
              </Row>
              
              <Row className="mb-3 g-3">
                <Col xs={12}>
                  <h6 className="mb-2" style={{ color: 'var(--bs-body-color)' }}>Фильтры</h6>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label className="small">Client IDs (через запятую или с новой строки)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="UUID, UUID, ..."
                    value={clientIds}
                    onChange={(e) => setClientIds(e.target.value)}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label className="small">Маркетплейсы</Form.Label>
                  <Form.Select
                    multiple
                    value={selectedMarketplaces}
                    onChange={(e) => {
                      const values = Array.from(e.target.selectedOptions, option => option.value);
                      setSelectedMarketplaces(values);
                    }}
                    size="3"
                  >
                    {marketplaceOptions.map(mp => (
                      <option key={mp} value={mp}>{mp}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted small">Удерживайте Ctrl/Cmd для выбора нескольких</Form.Text>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label className="small">Статусы (через запятую или с новой строки)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="status1, status2, ..."
                    value={statuses}
                    onChange={(e) => setStatuses(e.target.value)}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label className="small">Сервисы логистики</Form.Label>
                  <Form.Select
                    multiple
                    value={selectedLogisticServices}
                    onChange={(e) => {
                      const values = Array.from(e.target.selectedOptions, option => option.value);
                      setSelectedLogisticServices(values);
                    }}
                    size="3"
                  >
                    {logisticServiceOptions.map(service => (
                      <option key={service} value={service}>{service}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted small">Удерживайте Ctrl/Cmd для выбора нескольких</Form.Text>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label className="small">Поиск (номер заказа или штрихкод)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Поиск..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label className="small">Обновлено (дата и время)</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={updatedAt}
                    onChange={(e) => setUpdatedAt(e.target.value)}
                  />
                </Col>
                <Col xs={12}>
                  <Button variant="primary" onClick={handleApplyFilters} className="me-2">
                    <i className="bi bi-funnel me-1"></i>
                    Применить фильтры
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => {
                      setClientIds('');
                      setSelectedMarketplaces([]);
                      setStatuses('');
                      setSelectedLogisticServices([]);
                      setSearchFilter('');
                      setUpdatedAt('');
                      setDateFrom('');
                      setDateTo('');
                      setPeriodType('created_at');
                      setCurrentPage(1);
                    }}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Сбросить фильтры
                  </Button>
                </Col>
              </Row>

              {/* Returns Table */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : returns.length === 0 ? (
                <div className="text-center py-5" style={{ color: 'var(--bs-secondary-color)' }}>
                  <i className="bi bi-inbox display-4 d-block mb-2"></i>
                  Возвращенные заказы не найдены
                </div>
              ) : (
                <>
                  <div className="table-wrapper">
                    <Table hover>
                      <thead>
                        <tr>
                          <th style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>Маркетплейс</th>
                          <th style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>ID заказа</th>
                          <th style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>Товары</th>
                          <th style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {returns.map((returnItem, index) => {
                          const isExpanded = expandedRow === index;
                          const whRowHl = isWarehouseReturnRowHighlighted(returnItem);
                          return (
                            <React.Fragment key={index}>
                              <tr className={whRowHl ? 'admin-highlight-store-order' : undefined}>
                                <td style={{ color: 'var(--bs-body-color)' }}>
                                  <Badge bg="info">{returnItem.marketplace || '-'}</Badge>
                                </td>
                                <td style={{ color: 'var(--bs-body-color)' }}>
                                  <code>{returnItem.order_id || '-'}</code>
                                </td>
                                <td style={{ color: 'var(--bs-body-color)' }}>
                                  {returnItem.products && returnItem.products.length > 0 ? (
                                    <div>
                                      <div>{returnItem.products.length} товар(ов)</div>
                                      <small className="text-muted">
                                        {returnItem.products[0].marketplace_product_id && (
                                          <>ID: {returnItem.products[0].marketplace_product_id.substring(0, 8)}...</>
                                        )}
                                      </small>
                                    </div>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex gap-1 flex-wrap">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => setExpandedRow(isExpanded ? null : index)}
                                    >
                                      {isExpanded ? 'Скрыть' : 'Детали'}
                                    </Button>
                                    <Button
                                      variant="outline-info"
                                      size="sm"
                                      onClick={() => openSupportChat(returnItem)}
                                      title="Связаться с пользователем"
                                    >
                                      <i className="bi bi-chat-dots me-1"></i>
                                      Связаться
                                    </Button>
                                    <Button
                                      variant="outline-warning"
                                      size="sm"
                                      onClick={() => requestQRCode(returnItem)}
                                      title="Запросить QR код"
                                    >
                                      <i className="bi bi-qr-code me-1"></i>
                                      QR код
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr>
                                  <td colSpan="4" style={{ backgroundColor: 'var(--bs-tertiary-bg)', color: 'var(--bs-body-color)' }}>
                                    <Row className="p-3">
                                      <Col xs={12}>
                                        <h6 style={{ color: 'var(--bs-body-color)' }}>Детали возврата</h6>
                                        <div className="mb-2">
                                          <strong>Маркетплейс:</strong> {returnItem.marketplace || '-'}
                                        </div>
                                        <div className="mb-2">
                                          <strong>ID заказа:</strong> <code>{returnItem.order_id || '-'}</code>
                                        </div>
                                        {returnItem.products && returnItem.products.length > 0 && (
                                          <div className="mb-2">
                                            <strong>Товары:</strong>
                                            <ul className="mt-2">
                                              {returnItem.products.map((product, pIndex) => (
                                                <li key={pIndex}>
                                                  <div>
                                                    <strong>ID товара:</strong> <code>{product.marketplace_product_id || '-'}</code>
                                                  </div>
                                                  <div>
                                                    <strong>Количество:</strong> {product.quantity || 0}
                                                  </div>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                      </Col>
                                    </Row>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div style={{ color: 'var(--bs-body-color)' }}>
                        Показано {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalItems)} из {totalItems}
                      </div>
                      <Pagination>
                        <Pagination.First
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                        />
                        <Pagination.Prev
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        />
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                          let page;
                          if (totalPages <= 5) {
                            page = i + 1;
                          } else if (currentPage <= 3) {
                            page = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            page = totalPages - 4 + i;
                          } else {
                            page = currentPage - 2 + i;
                          }
                          return (
                            <Pagination.Item
                              key={page}
                              active={page === currentPage}
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </Pagination.Item>
                          );
                        })}
                        <Pagination.Next
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        />
                        <Pagination.Last
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
          </>
        </Card.Body>
      </Card>

    </Container>
  );
};

export default WarehouseReturnsPage;


